import { atom, useSetAtom } from 'jotai';
import { atomFamily } from 'jotai-family';
import * as React from 'react';

import type { TableCellTarget } from '@gitbook/api-client';
import { conflicts } from '@gitbook/doc-core';

interface HoveredCommentNodeState {
    pageId: string;
    node: string;
    /** Show button to post button */
    postButton: boolean;
    /** Show highlighting background on the node */
    highlighting: boolean;
}

/**
 * State storing the document node to highlight for commenting.
 */
export const hoveredCommentNodeAtom = atom<null | HoveredCommentNodeState>(null);

/**
 * Identify a comment hover target. Empty when the node isn't addressable, which resolves to
 * "not hovered".
 *
 * `isCommentNodeHoveredSelector` is keyed by this string rather than by a `{ pageId, nodeOrCellKey }`
 * object on purpose: `atomFamily` is an O(1) `Map` lookup for primitive params, but falls back to a
 * linear scan over every entry as soon as it needs a custom comparator. Its callers run once per
 * block, so an object key makes a full editor render quadratic in the number of blocks.
 */
export function getHoveredCommentTargetKey(
    pageId: string | null | undefined,
    nodeOrCellKey: string | null | undefined
): string {
    return pageId && nodeOrCellKey ? `${pageId}:${nodeOrCellKey}` : '';
}

/**
 * Jotai selector to test if a node is the currently hovered node for comments.
 */
export const isCommentNodeHoveredSelector = atomFamily((target: string) =>
    atom((get) => {
        if (!target) {
            return false;
        }
        const hoveredNode = get(hoveredCommentNodeAtom);
        return Boolean(
            hoveredNode?.highlighting &&
            getHoveredCommentTargetKey(hoveredNode.pageId, hoveredNode.node) === target
        );
    })
);

/**
 * React hook to update the current highlighted node.
 */
export function useSetHoveredCommentNode(): {
    highlight: (
        target: { pageId: string; node: string } | null,
        params?: Omit<HoveredCommentNodeState, 'node' | 'pageId'>
    ) => void;
} {
    const setHoveredCommentNode = useSetAtom(hoveredCommentNodeAtom);

    return React.useMemo(() => {
        return {
            highlight: (
                target,
                params = {
                    highlighting: true,
                    postButton: true,
                }
            ) => {
                const node = target?.node ? conflicts.getOriginalKey(target.node) : null;
                const pageId = target?.pageId;

                setHoveredCommentNode((prev) => {
                    if (!node || !pageId) {
                        return null;
                    }

                    const nextPostButton =
                        prev && prev.node === node && prev.pageId === pageId && prev.postButton
                            ? prev.postButton
                            : params.postButton;

                    return {
                        node,
                        pageId,
                        // if we were showing the postButton for the same node,
                        // we will still show it, regardless of the params
                        // in order to avoid layout shifts.
                        ...params,
                        postButton: nextPostButton,
                    };
                });
            },
        };
    }, [setHoveredCommentNode]);
}

// Serialize the record and definition so we don't need to use a complex object to create a unique key for a table cell
export function getSerializedTableCellKey(tableCell?: TableCellTarget) {
    return tableCell ? `${tableCell.record}:${tableCell.definition}` : undefined;
}
